/**
 * helpers.js - Utilidades de limpieza y formato para importadores y reportes.
 * CORRECCIÓN CRÍTICA: Se implementaron métodos UTC para evitar el salto de fecha
 * por zona horaria (evita que el 01/02 se transforme en 31/01).
 */

export const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    const num = parseFloat(value);
    const isNegative = num < 0;
    const absValue = Math.abs(num);
    const formatted = new Intl.NumberFormat('es-AR', { 
        style: 'currency', 
        currency: 'ARS',
        minimumFractionDigits: 2 
    }).format(absValue);
    return isNegative ? `-${formatted}` : formatted;
};

export const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${parseFloat(value).toFixed(2)}%`;
};

export const normalizeText = (str) => {
    if (!str) return '';
    return String(str).toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

/**
 * parseImporteExcel - Limpieza profunda de valores monetarios.
 */
export const parseImporteExcel = (raw) => {
    if (raw === undefined || raw === null || raw === '') return 0;
    if (typeof raw === 'number') return raw;
    
    let str = String(raw).trim();
    const isNegative = str.includes('-') || (str.startsWith('(') && str.endsWith(')'));
    str = str.replace(/[^\d,.]/g, '');
    
    if (str.includes(',') && str.includes('.')) {
        str = str.replace(/\./g, '').replace(',', '.');
    } else if (str.includes(',')) {
        str = str.replace(',', '.');
    }
    
    const number = parseFloat(str);
    if (isNaN(number)) return 0;
    return isNegative ? -Math.abs(number) : Math.abs(number);
};

export const normalizePoliza = (nro) => {
    if (nro === undefined || nro === null || nro === '') return '';
    let str = String(nro).trim();
    str = str.replace(/[-\s]/g, '');
    if (str.includes('.')) {
        str = str.split('.')[0];
    }
    return str;
};

/**
 * normalizePeriodo - Estandarización forzada a DD/MM/YYYY (Día siempre 01).
 * SOLUCIÓN TIMEZONE: Utiliza getUTC... para ignorar el desfase horario de la PC.
 */
export const normalizePeriodo = (raw) => {
    if (!raw) return 'S/D';
    
    // Caso 1: Objeto Date de JS (a veces lo devuelve la librería XLSX)
    if (raw instanceof Date) {
        const m = String(raw.getUTCMonth() + 1).padStart(2, '0');
        const y = raw.getUTCFullYear();
        return `01/${m}/${y}`;
    }

    // Caso 2: Número serial de Excel (ej: 46054)
    if (typeof raw === 'number' && raw > 30000) {
        // El serial de Excel se mide en días desde 1899-12-30.
        // Al crear el Date así, se genera a las 00:00:00 UTC.
        const date = new Date(Math.round((raw - 25569) * 86400 * 1000));
        
        // USAMOS UTC PARA EXTRAER LOS DATOS (Ignora si la PC está en GMT-3)
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const y = date.getUTCFullYear();
        return `01/${m}/${y}`;
    }

    // Caso 3: String (ej: "01/02/2026" o "02/2026")
    let str = String(raw).trim();
    
    // Manejo de formato MM/YYYY o MM/YY
    const monthYearMatch = str.match(/^(\d{1,2})\/(\d{2,4})$/);
    if (monthYearMatch) {
        const m = monthYearMatch[1].padStart(2, '0');
        const y = monthYearMatch[2];
        const yearFull = y.length === 2 ? `20${y}` : y;
        return `01/${m}/${yearFull}`;
    }

    // Manejo de formato DD/MM/YYYY o DD/MM/YY
    if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length === 3) {
            const m = parts[1].padStart(2, '0');
            const y = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
            return `01/${m}/${y}`;
        }
    }

    // Manejo de formato ISO YYYY-MM-DD
    if (str.includes('-')) {
        const parts = str.split('-');
        if (parts.length === 3) {
            const m = parts[1].padStart(2, '0');
            const y = parts[0];
            return `01/${m}/${y}`;
        }
    }
    
    return str; 
};